import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState, useCallback } from 'react'
import { toast } from 'sonner'
import { BRAND_MINT, formatCentsWithCurrency } from '@/lib/utils'
import { getAdminOrgAddOnsApi, type AdminOrgAddOn } from '@/lib/billing'
import {
  LoaderIcon,
  SearchIcon,
  XIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ShoppingCartIcon,
  PackageIcon,
  TrendingUpIcon,
  ShieldCheckIcon,
} from 'lucide-react'

const STATUS_STYLES: Record<string, { label: string; color: string }> = {
  active:    { label: 'Active', color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
  cancelled: { label: 'Cancelled', color: 'bg-muted text-muted-foreground' },
  expired:   { label: 'Expired', color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
}

const BILLING_MODEL_LABELS: Record<string, string> = {
  monthly_recurring: 'Monthly',
  annual_recurring: 'Annual',
  one_time: 'One-time',
}


function formatDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  })
}

export const Route = createFileRoute('/dashboard/admin/org-add-ons')({
  component: () => {
    const [addOns, setAddOns] = useState<AdminOrgAddOn[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [loading, setLoading] = useState(true)

    // Filters
    const [orgIdInput, setOrgIdInput] = useState('')
    const [orgId, setOrgId] = useState<number | undefined>(undefined)
    const [statusFilter, setStatusFilter] = useState('')

    const limit = 50

    const fetchAddOns = useCallback(async () => {
      setLoading(true)
      try {
        const params: Record<string, string | number> = { page, limit }
        if (orgId) params.org_id = orgId
        if (statusFilter) params.status = statusFilter
        const res = await getAdminOrgAddOnsApi(params)
        setAddOns(res.data)
        setTotal(res.total)
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to load add-on purchases')
      } finally {
        setLoading(false)
      }
    }, [page, orgId, statusFilter])

    useEffect(() => { fetchAddOns() }, [fetchAddOns])

    const handleOrgFilter = () => {
      const id = parseInt(orgIdInput, 10)
      setOrgId(isNaN(id) || id <= 0 ? undefined : id)
      setPage(1)
    }

    const handleClearFilters = () => {
      setOrgId(undefined)
      setOrgIdInput('')
      setStatusFilter('')
      setPage(1)
    }

    const totalPages = Math.max(1, Math.ceil(total / limit))
    const hasFilters = orgId !== undefined || statusFilter !== ''

    if (loading && addOns.length === 0) {
      return (
        <div className="mx-auto max-w-5xl">
          <div className="mb-8">
            <div className="flex items-start gap-3">
              <span aria-hidden="true" className="mt-0.5 shrink-0" style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }} />
              <h1 className="text-2xl font-semibold">Add-on purchases</h1>
            </div>
          </div>
          <div className="flex items-center justify-center rounded-xl border border-border bg-card py-16">
            <LoaderIcon className="size-5 animate-spin text-muted-foreground" />
            <p className="ml-2 text-sm text-muted-foreground">Loading purchases…</p>
          </div>
        </div>
      )
    }

    return (
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <div className="flex items-start gap-3">
            <span aria-hidden="true" className="mt-0.5 shrink-0" style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }} />
            <div>
              <h1 className="text-2xl font-semibold">Add-on purchases</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                View all add-on purchases across organizations. Filter by org or status.
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-wrap gap-2">
          <div className="flex gap-2">
            <input
              type="number"
              value={orgIdInput}
              onChange={(e) => setOrgIdInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleOrgFilter()}
              placeholder="Org ID…"
              className="w-28 rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
            />
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary"
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="cancelled">Cancelled</option>
              <option value="expired">Expired</option>
            </select>
            <button
              type="button"
              onClick={handleOrgFilter}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              <SearchIcon className="size-4" />Filter
            </button>
            {hasFilters && (
              <button
                type="button"
                onClick={handleClearFilters}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
              >
                <XIcon className="size-4" />Clear
              </button>
            )}
          </div>
        </div>

        <p className="mb-4 text-xs text-muted-foreground">
          {total} purchase{total !== 1 ? 's' : ''}
          {orgId !== undefined && ` · org #${orgId}`}
          {statusFilter && ` · ${statusFilter}`}
        </p>

        {/* Table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">Organization</th>
                <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">Add-on</th>
                <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">Billing</th>
                <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">Status</th>
                <th className="px-4 py-3 text-right text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">Qty</th>
                <th className="px-4 py-3 text-right text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">Price</th>
                <th className="px-4 py-3 text-right text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">Purchased</th>
              </tr>
            </thead>
            <tbody>
              {addOns.map((a) => {
                const s = STATUS_STYLES[a.status] ?? { label: a.status, color: 'bg-muted text-muted-foreground' }
                return (
                  <tr key={a.org_add_on_id} className="border-b border-border/50 last:border-0 hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <PackageIcon className="size-4 shrink-0 text-muted-foreground" />
                        <span className="font-medium text-foreground">{a.org_name}</span>
                        <span className="text-[10px] text-muted-foreground">#{a.org_id}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className="font-medium text-foreground">{a.display_name}</span>
                        <span className="text-[10px] text-muted-foreground">{a.add_on_key}</span>
                      </div>
                      {a.description && (
                        <p className="mt-0.5 text-[10px] text-muted-foreground line-clamp-1">{a.description}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-foreground">{BILLING_MODEL_LABELS[a.billing_model] || a.billing_model}</span>
                        {a.tier_required && (
                          <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase ${
                            a.tier_required === 'enterprise'
                              ? 'bg-purple-500/10 text-purple-600 dark:text-purple-400'
                              : 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
                          }`}>
                            {a.tier_required === 'enterprise' ? <ShieldCheckIcon className="size-2.5" /> : <TrendingUpIcon className="size-2.5" />}
                            {a.tier_required}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${s.color}`}>
                        {a.status === 'active' && <CheckCircleIcon className="size-3" />}
                        {a.status === 'cancelled' && <XCircleIcon className="size-3" />}
                        {a.status === 'expired' && <ClockIcon className="size-3" />}
                        {s.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-xs tabular-nums">{a.quantity}</td>
                    <td className="px-4 py-3 text-right text-xs tabular-nums font-medium">
                      {formatCentsWithCurrency(a.unit_price_cents, 'EUR')}
                      {a.quantity > 1 && (
                        <span className="ml-1 text-[10px] text-muted-foreground">
                          ({formatCentsWithCurrency(a.unit_price_cents * a.quantity, 'EUR')} total)
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-muted-foreground">{formatDate(a.purchased_at)}</td>
                  </tr>
                )
              })}
              {addOns.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-16 text-center text-sm text-muted-foreground">
                    <ShoppingCartIcon className="mx-auto mb-2 size-6 text-muted-foreground/40" />
                    No add-on purchases found{hasFilters ? ' matching the current filters' : ''}.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-center gap-3">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-3 py-1.5 text-sm text-foreground hover:bg-muted disabled:opacity-40"
            >
              <ChevronLeftIcon className="size-4" />Previous
            </button>
            <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-3 py-1.5 text-sm text-foreground hover:bg-muted disabled:opacity-40"
            >
              Next<ChevronRightIcon className="size-4" />
            </button>
          </div>
        )}
      </div>
    )
  },
})
